import type ChartCardStore from "../ChartCard/ChartCardStore";
import type { ReorderAudience } from "./Types";

/** Audience a chart's reorder section targets when its config doesn't specify one. */
export const DEFAULT_REORDER_AUDIENCE: ReorderAudience = "internal";

/** The chart's effective reorder audience (config value, or the default). */
export const resolveReorderAudience = (
    chartStore: ChartCardStore | null | undefined,
): ReorderAudience =>
    chartStore?.config.reorder?.audience ?? DEFAULT_REORDER_AUDIENCE;

/**
 * Single source of truth for whether a chart's reorder section should render. Combines the structural
 * rules (it's a reorder-opted-in chart with a persisted, value-list order rule) with the audience policy.
 * Callers supply `isInternalUser` so this stays decoupled from the auth store.
 */
export const canReorderChart = (
    chartStore: ChartCardStore | null | undefined,
    context: { isInternalUser: boolean },
): boolean => {
    if (!chartStore) return false;
    const config = chartStore.config.reorder;
    if (!config) return false; // chart opted in?
    if (!chartStore.orderRuleId) return false; // a persisted rule to save to?
    if (!Array.isArray(chartStore.orderRule?.orderByValues)) return false; // reorderable values?

    const audience = config.audience ?? DEFAULT_REORDER_AUDIENCE;
    return audience === "everyone" || context.isInternalUser;
};
